import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDropdown from '../../components/GenericDropDown';
import GenericInput from '../../components/GenericInput';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { addBet, getAllActiveGameConfigs, scheduleBet } from '../../redux/entities/gameEntity';
import { getUserBalance } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

const PageHeading = styled.h2`
  margin: 0;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2.5rem;
  letter-spacing: 0.5px;
`;

const PageHeaderRow = styled.div`
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ClockChip = styled.div`
  border: 1px solid #d6e3f6;
  border-radius: 999px;
  background: #f3f8ff;
  color: #2a4d75;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 8px 12px;
`;

const TableWrapper = styled.div`
  overflow: visible;
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
`;

const GameSelectionWrapper = styled.div`
  margin-bottom: 1.25rem;
`;

const StepTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin: 0 0 0.75rem;
`;

const GameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 14px;
`;

const GameCard = styled.button<{ $accent: string; $selected: boolean }>`
  border: 1px solid ${({ $selected, $accent }) => ($selected ? $accent : 'rgba(0,0,0,0.08)')};
  border-radius: 16px;
  background: ${({ $selected, $accent }) =>
    $selected
      ? `linear-gradient(180deg, ${$accent}22 0%, #ffffff 100%)`
      : '#ffffff'};
  box-shadow: ${({ $selected }) =>
    $selected
      ? '0 8px 24px rgba(0, 0, 0, 0.16)'
      : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  padding: 14px 10px 10px;
  width: 100%;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const GameThumb = styled.div<{ $accent: string }>`
  width: 66px;
  height: 66px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  background: ${({ $accent }) => `linear-gradient(145deg, ${$accent} 0%, ${$accent}cc 100%)`};
  color: #ffffff;
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.4px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.32);
`;

const GameLabel = styled.div<{ $accent: string }>`
  width: 100%;
  border-radius: 10px;
  background: ${({ $accent }) => `${$accent}1f`};
  color: ${({ theme }) => theme.text};
  font-size: 0.88rem;
  line-height: 1.2;
  font-weight: 600;
  padding: 8px 6px;
`;

const SelectedGameHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 0.8rem;
  flex-wrap: wrap;
`;

const SelectedGameChip = styled.div<{ $accent: string }>`
  border-radius: 999px;
  border: 1px solid ${({ $accent }) => `${$accent}66`};
  background: ${({ $accent }) => `${$accent}1a`};
  color: ${({ theme }) => theme.text};
  font-weight: 600;
  font-size: 0.92rem;
  padding: 8px 12px;
`;

const ChangeGameButton = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 999px;
  background: #ffffff;
  color: ${({ theme }) => theme.text};
  font-size: 0.9rem;
  padding: 7px 12px;
  cursor: pointer;
`;

const BurfiContainer = styled.div`
  display: grid;
  grid-template-areas:
    ".  n1  ."
    "n4 n5 n2"
    ".  n3  .";
  gap: 10px;
  width: fit-content;
  margin: 20px auto;
`;

const BurfiInput = styled(GenericInput) <{ $gridArea: string }>`
  width: 64px;
  margin: 0 auto;
  grid-area: ${props => props.$gridArea};
  label {
    text-align: center;
    font-size: 0.85rem;
    font-weight: 600;
  }
  input {
    text-align: center;
    padding: 8px 4px;
    width: 64px;
    height: 60px;
    border-radius: 8px;
  }
`;


const PlayGamePage: React.FC = () => {
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [selectedResultTime, setSelectedResultTime] = useState<string>('');
  const [selectedGameType, setSelectedGameType] = useState<string>('');
  const [betNumber, setBetNumber] = useState<string>('');
  const [points, setPoints] = useState<string>('');
  const [allResultTimes, setAllResultTimes] = useState<Array<{ value: string; label: string }>>([]);
  const [resultTimeOptions, setResultTimeOptions] = useState<any[]>([]);
  const [fd, setFd] = useState('');
  const [enableScheduling, setEnableScheduling] = useState(false);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [futureTimeSlots, setFutureTimeSlots] = useState<Array<{ value: string, label: string }>>([]);
  const [ld, setLd] = useState('');
  const [op, setOp] = useState('');
  const [ep, setEp] = useState('');
  const [r, setR] = useState('');
  const [g, setG] = useState('');
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  const [gameTypeOptions] = useState<any[]>([
    { label: 'Single', value: 'Single' },
    { label: 'Patti', value: 'Patti' },
    { label: 'Digit', value: 'Digit' },
    { label: 'Burfi', value: 'Burfi' },
  ]);

  useEffect(() => {
    const generateBetNumber = () => {
      const fdVal = fd || '99999';
      const ldVal = ld || '99999';
      const opVal = op || '99999';
      const epVal = ep || '99999';
      const rVal = r || '99999';
      const gVal = g || '99999';

      setBetNumber(`${fdVal}*${ldVal}*${opVal}*${epVal}*${rVal}*${gVal}`);
    };

    generateBetNumber();
  }, [fd, ld, op, ep, r, g]);

  const dispatch = useAppDispatch();
  const { showModalStrip } = useModalStrip();
  const gameConfigs = useAppSelector((state) => state.entities.gameReducer.activeGameConfigs);

  useEffect(() => {
    dispatch(getAllActiveGameConfigs()); // Fetch game configurations from Redux
  }, [dispatch]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const groupedGameConfigs = useMemo(
    () =>
      gameConfigs
        .filter((x) => x.status === 'ACTIVE' && !x.game.offline)
        .reduce((acc, config) => {
          const gameName = config.game.name;
          if (!acc[gameName]) {
            acc[gameName] = [];
          }
          acc[gameName].push(config);
          return acc;
        }, {} as Record<string, any[]>),
    [gameConfigs]
  );

  const gameOptions = useMemo(
    () =>
      Object.keys(groupedGameConfigs).map((gameName) => ({
        value: gameName,
        label: gameName,
      })),
    [groupedGameConfigs]
  );

  const getGameColor = (gameName: string) => {
    const normalized = gameName.trim().toLowerCase();
    if (normalized.includes('mumbai matka')) return '#e53935';
    if (normalized.includes('kalyan bazar')) return '#1b5e20';
    if (normalized === 'cm' || normalized.includes(' cm ') || normalized.includes('cm ')) return '#29b6f6';
    if (normalized.includes('nagmani')) return '#66bb6a';
    if (normalized.includes('nagpur main')) return '#00838f';
    return '#4a90e2';
  };

  const getGameInitials = (gameName: string) => {
    const parts = gameName.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'GM';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  // Get the resultTimes based on selected game
  const getResultTimes = useCallback(
    (gameName: string) => {
      const selectedGameConfig = groupedGameConfigs[gameName] || [];
      return selectedGameConfig.map((config: any) => config.resultTime);
    },
    [groupedGameConfigs]
  );

  const parseResultTimeToDate = useCallback((timeLabel: string) => {
    const [time, period] = timeLabel.split(' ');
    const [rawHour, minutes] = time.split(':').map(Number);
    let hours = rawHour;
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }, []);

  useEffect(() => {
    if (selectedGame) {
      setSelectedResultTime('');
      setSelectedGameType('');
      setEnableScheduling(false);
      setSelectedTimeSlots([]);
      setFutureTimeSlots([]);
      setFd('');
      setLd('');
      setOp('');
      setEp('');
      setR('');
      setG('');
      setBetNumber('');
      setPoints('');
      const resultTimes = getResultTimes(selectedGame).map(resultTime => ({
        value: resultTime.id.toString(),
        label: resultTime.resultTime,
      }));

      // Sorting the times based on the label (which is in 12-hour format like "10:30 AM")
      const sortedResultTimes = resultTimes.sort((a, b) => {
        const timeA = new Date(`01/01/2020 ${a.label}`); // Adding a fixed date to parse the time correctly
        const timeB = new Date(`01/01/2020 ${b.label}`);
        return timeA.getTime() - timeB.getTime();
      });

      setAllResultTimes(sortedResultTimes);
    } else {
      setAllResultTimes([]);
      setResultTimeOptions([]);
    }
  }, [getResultTimes, selectedGame]);

  useEffect(() => {
    if (!selectedGame || allResultTimes.length === 0) {
      setResultTimeOptions([]);
      return;
    }
    const now = currentTime;
    const immediateUpcoming = allResultTimes.find((option) => parseResultTimeToDate(option.label) > now);
    setResultTimeOptions(immediateUpcoming ? [immediateUpcoming] : []);
  }, [allResultTimes, currentTime, parseResultTimeToDate, selectedGame]);


  const getGameTypeOptions = () => {
    let game = groupedGameConfigs[selectedGame]
      .map((config: any) => config.game)[0]; // Access the first game object

    if (game?.burfiSupported) {
      return gameTypeOptions.filter(x => x.value === 'Burfi');
    } else if (game?.digitSupported) {
      return gameTypeOptions.filter(x => x.value !== 'Burfi');
    } else {
      return gameTypeOptions.filter(x => x.value !== 'Burfi' && x.value !== 'Digit');
    }
  }

  // Update future time slots when result time changes
  useEffect(() => {
    if (selectedResultTime) {
      const selectedTime = resultTimeOptions.find(opt => opt.value === selectedResultTime);
      if (selectedTime) {
        const now = new Date();
        const currentHours = now.getHours();
        const currentMinutes = now.getMinutes();

        const futureSlots = resultTimeOptions
          .filter(opt => {
            // Skip the currently selected time
            if (opt.value === selectedResultTime) return false;

            const [time, period] = opt.label.split(' ');
            let [hours, minutes] = time.split(':').map(Number);

            // Convert to 24-hour format for comparison
            if (period === 'PM' && hours < 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;

            // Compare with current time
            if (hours > currentHours || (hours === currentHours && minutes > currentMinutes)) {
              return true;
            }
            return false;
          })
          .map(opt => ({
            value: opt.value,
            label: opt.label
          }));

        setFutureTimeSlots(futureSlots);
        // Reset selected time slots when result time changes
        setSelectedTimeSlots([]);
      }
    } else {
      setFutureTimeSlots([]);
      setSelectedTimeSlots([]);
    }
  }, [selectedResultTime, resultTimeOptions]);

  useEffect(() => {
    if (selectedResultTime && !resultTimeOptions.some((opt) => opt.value === selectedResultTime)) {
      setSelectedResultTime('');
      setSelectedGameType('');
      setEnableScheduling(false);
      setSelectedTimeSlots([]);
    }
  }, [resultTimeOptions, selectedResultTime]);

  // Handle form submission (save bet)
  const handleSaveBet = () => {

    console.log(selectedTimeSlots)

    // Validation
    if (!selectedGame || !selectedResultTime || !selectedGameType || !betNumber || !points) {
      alert('All fields are required');
      return;
    }

    // Prepare the request object
    const betData = {
      game: selectedGame,
      resultTime: selectedResultTime,
      gameType: selectedGameType,
      betNumber,
      point: points,
      gameConfig: groupedGameConfigs[selectedGame]?.find(
        (config) => config.resultTime.id.toString() === selectedResultTime
      ), // Get the correct gameConfig based on the selected game and result time
    };

    const scheduleBetData = {
      gameType: selectedGameType,
      betNumber,
      point: points,
      gameConfig: groupedGameConfigs[selectedGame]?.filter(
        (config) => selectedTimeSlots.includes(config.resultTime.id.toString())
      ), // Get the correct gameConfig based on the selected game and result time
    };

    // Dispatch action to save the bet
    dispatch(addBet(betData)).unwrap()
      .then((res: any) => {
        showModalStrip('success', 'Bet Created successfully', 2000);
        setEnableScheduling(false);
        setSelectedTimeSlots([]);
        setBetNumber('')
        setPoints('')
        setFd('');
        setLd('');
        setOp('');
        setEp('');
        setR('');
        setG('');
        dispatch(getUserBalance());
      })
      .catch((err: Error) => {
        console.log(err);
        showModalStrip('danger', err.message, 5000);
      });

    if (enableScheduling && selectedTimeSlots.length > 0) {
      dispatch(scheduleBet(scheduleBetData)).unwrap()
        .then((res: any) => {
          showModalStrip('success', 'Bet Scheduled successfully', 2000);
          setEnableScheduling(false);
          setSelectedTimeSlots([]);
          setBetNumber('');
          setPoints('');
          setFd('');
          setLd('');
          setOp('');
          setEp('');
          setR('');
          setG('');
          dispatch(getUserBalance());
        })
        .catch((err: Error) => {
          console.log(err);
          showModalStrip('danger', err.message, 5000);
        });
    }


  };

  const validateBurfiInput = (value: string, type: 'FD' | 'LD' | 'Single' | 'OP' | 'EP' | 'Barfi') => {
    const numValue = value.replace(/[^0-9]/g, '');
    switch (type) {
      case 'FD':
      case 'LD':
      case 'Single':
        return numValue.slice(0, 1);
      case 'OP':
      case 'EP':
        return numValue.slice(0, 2);
      case 'Barfi':
        return numValue.slice(0, 4);
      default:
        return numValue;
    }
  };

  const renderBurfiInput = () => {
    return (
      <BurfiContainer>
        <BurfiInput
          $gridArea="n1"
          value={fd}
          onChange={(e: string) => setFd(validateBurfiInput(e, 'FD'))}
          label='FD'
          maxLength={1}
        />
        <BurfiInput
          $gridArea="n2"
          value={ld}
          onChange={(e: string) => setLd(validateBurfiInput(e, 'LD'))}
          label='LD'
          maxLength={1}
        />
        <BurfiInput
          $gridArea="n3"
          value={op}
          onChange={(e: string) => setOp(validateBurfiInput(e, 'OP'))}
          label='OP'
          maxLength={2}
        />
        <BurfiInput
          $gridArea="n4"
          value={ep}
          onChange={(e: string) => setEp(validateBurfiInput(e, 'EP'))}
          label='EP'
          maxLength={2}
        />
        <BurfiInput
          $gridArea="n5"
          value={r}
          onChange={(e: string) => setR(validateBurfiInput(e, 'Single'))}
          label='S'
          maxLength={1}
        />
        <BurfiInput
          $gridArea="n6"
          value={g}
          onChange={(e: string) => setG(validateBurfiInput(e, 'Barfi'))}
          label='Burfi'
          maxLength={4}
        />
      </BurfiContainer>
    );
  };

  return (
    <Container>
      <PageHeaderRow>
        <PageHeading>Place Your Bet</PageHeading>
        <ClockChip>{currentTime.toLocaleTimeString()}</ClockChip>
      </PageHeaderRow>

      <TableWrapper>
        {!selectedGame ? (
          <GameSelectionWrapper>
            <StepTitle>Select Game</StepTitle>
            <GameGrid>
              {gameOptions.map((game) => {
                const accent = getGameColor(game.value);
                return (
                  <GameCard
                    key={game.value}
                    type="button"
                    $accent={accent}
                    $selected={selectedGame === game.value}
                    onClick={() => setSelectedGame(game.value)}
                    aria-label={`Select ${game.label}`}
                  >
                    <GameThumb $accent={accent}>{getGameInitials(game.label)}</GameThumb>
                    <GameLabel $accent={accent}>{game.label}</GameLabel>
                  </GameCard>
                );
              })}
            </GameGrid>
          </GameSelectionWrapper>
        ) : (
          <SelectedGameHeader>
            <SelectedGameChip $accent={getGameColor(selectedGame)}>
              {selectedGame}
            </SelectedGameChip>
            <ChangeGameButton
              type="button"
              onClick={() => {
                setSelectedGame('');
                setSelectedResultTime('');
                setSelectedGameType('');
                setEnableScheduling(false);
                setSelectedTimeSlots([]);
                setResultTimeOptions([]);
              }}
            >
              Change Game
            </ChangeGameButton>
          </SelectedGameHeader>
        )}

        {/* Select Result Time */}
        {selectedGame && (
          <GenericDropdown
            label="Select Result Time"
            options={resultTimeOptions}
            onChange={(resultTime) => setSelectedResultTime(resultTime)}
            value={selectedResultTime}
          />
        )}

        {/* Select Game Type */}
        {selectedResultTime && (
          <GenericDropdown
            label="Select Game Type"
            options={getGameTypeOptions()}
            onChange={(gameType) => setSelectedGameType(gameType)}
            value={selectedGameType}
          />
        )}

        {/* Bet Number Input */}
        {selectedGameType && (
          <>
            {selectedGameType === 'Barfi' ? (
              <div>
                <label>Bet Numbers (Barfi Pattern)</label>
                {renderBurfiInput()}
              </div>
            ) : (
              <GenericInput
                label="Bet Numbers"
                value={betNumber}
                placeholder='Add * symbol for multiple bet'
                onChange={(e: string) => setBetNumber(e)}
              />
            )}
          </>
        )}

        {/* Bet Points Input */}
        {selectedGameType && (
          <GenericInput
            label="Bet Points"
            placeholder='Add bet point'
            value={points}
            onChange={(e: string) => setPoints(e)}
          />
        )}

        {/* Submit Button */}
        {selectedResultTime && (
          <GenericButton
            label="Place Bet"
            onClick={handleSaveBet}
          />
        )}
      </TableWrapper>
    </Container>
  );
};

export default PlayGamePage;
